import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HoverTip } from 'shared/components/HoverTip';
import { RankPrefix } from 'shared/components/RankPrefix';
import { RULES_SETUP_URL } from 'features/auth/SlackLoginScreen';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { isTutorialComplete, subscribeTutorialProgress } from 'features/battle/index';
import { claimPath, pathForNav } from 'shared/lib/nav-paths';
import { compareBadgesByScoreAsc } from 'features/catalog/rarity';
import './Sidebar.css';

export type ShellNavId =
  | 'feed'
  | 'collected'
  | 'claim'
  | 'journeys'
  | 'profile_feed'
  | 'submitted'
  | 'leaderboard'
  | 'explore'
  | 'badge_gallery'
  | 'journey_gallery'
  | 'stats'
  | 'badge_hashes'
  | 'badges_battle'
  | 'battle_how_to'
  | 'battle_arena'
  | 'battle_leaderboards';

/** Location state when opening the leaderboard focused on a profile. */
export type LeaderboardExpandState = {
  expandProfileId?: string;
};

interface SidebarProps {
  profile: Profile | null;
  avatarUrl?: string;
  activeNav: ShellNavId;
  submissionsCount: number;
  feedCount?: number;
  /** Windowed in-progress battle: icon rail that overlays content when expanded. */
  overlayRail?: boolean;
  onNavigate: (nav: ShellNavId) => void;
  onClaimNewBadges: () => void;
  onSubmitBadgeIdea: () => void;
  onSignOut: () => void;
}

const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const IconHome = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 10.5L12 4l8 6.5V20a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1v-9.5z" />
  </svg>
);
const IconAward = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <circle cx="12" cy="9" r="5" />
    <path d="M8.5 13.5L7 21l5-2.5L17 21l-1.5-7.5" />
  </svg>
);
const IconFlag = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M5 21V4m0 0h10l-1.5 4L19 12H5" />
  </svg>
);
const IconRosette = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <circle cx="12" cy="10" r="5" />
    <path d="M9.5 14.5L8 21l4-2 4 2-1.5-6.5" />
  </svg>
);
const IconTrophy = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 6H4a2 2 0 002 4M17 6h3a2 2 0 01-2 4" />
  </svg>
);
const IconGrid = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <rect x="14" y="14" width="6" height="6" rx="1" />
  </svg>
);
const IconChart = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M5 19V9M12 19V5M19 19v-7" />
  </svg>
);
const IconSearch = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16.5 16.5L21 21" />
  </svg>
);
const IconBattle = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M5 19L10 4l3 6 6-3-5 16-3-6-6 2z" />
    <path d="M10 10l4 4" />
  </svg>
);
const IconArena = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <rect x="4" y="7" width="10" height="13" rx="1.5" />
    <rect x="8" y="4" width="10" height="13" rx="1.5" />
  </svg>
);
const IconFeed = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);
const IconSignOut = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M10 7V5a2 2 0 012-2h7a2 2 0 012 2v14a2 2 0 01-2 2h-7a2 2 0 01-2-2v-2M15 12H3m0 0l3-3m-3 3l3 3" />
  </svg>
);
const IconBook = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke}>
    <path d="M5 5.5A2.5 2.5 0 017.5 3H20v16H7.5A2.5 2.5 0 005 16.5v-11z" />
    <path d="M5 16.5A2.5 2.5 0 017.5 19H20" />
  </svg>
);
const IconMenu = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke} aria-hidden>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);
const IconClose = () => (
  <svg className="tb-nav-icon" viewBox="0 0 24 24" {...stroke} aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const monogram = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

const isDesktopNav = (): boolean => typeof window !== 'undefined' && window.matchMedia('(min-width: 761px)').matches;

const isWindowedMatchRail = (): boolean =>
  isDesktopNav() &&
  !document.documentElement.classList.contains('bb-match-fullscreen') &&
  Boolean(document.querySelector('.bb-page.is-playing'));

export const Sidebar: React.FC<SidebarProps> = ({
  profile,
  activeNav,
  submissionsCount,
  feedCount = 0,
  overlayRail = false,
  onNavigate: _onNavigate,
  onClaimNewBadges: _onClaimNewBadges,
  onSubmitBadgeIdea,
  onSignOut,
}) => {
  const { rankedProfiles, badgesMap } = useTalosData();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [tutorialDone, setTutorialDone] = useState(isTutorialComplete);
  const badgeCount = profile?.badgesIDs.length ?? 0;
  const journeyCount = profile?.journeyEmojis.length ?? 0;
  const score = profile?.sortScore ?? 0;
  const displayName = profile?.name?.trim() || 'No Profile';
  const verifiedEmail = profile?.email ?? '';
  const profileId = profile ? getFirstPartOfEmail(profile.email) : null;

  const rank = useMemo(() => {
    if (!profile) return null;
    const index = rankedProfiles.findIndex((p) => p.email === profile.email);
    return index >= 0 ? index + 1 : null;
  }, [profile, rankedProfiles]);

  const coolestBadge = useMemo(() => {
    if (!profile) return null;
    return (
      profile.badgesIDs
        .map((id) => badgesMap.get(id))
        .filter((b): b is Badge => !!b)
        .sort(compareBadgesByScoreAsc)[0] ?? null
    );
  }, [profile, badgesMap]);

  useEffect(() => {
    setMenuOpen(false);
    setRailOpen(false);
  }, [location.pathname]);

  useEffect(() => subscribeTutorialProgress(() => setTutorialDone(isTutorialComplete())), []);

  useEffect(() => {
    if (!overlayRail) {
      setRailOpen(false);
      return;
    }
    const sync = () => {
      if (!isWindowedMatchRail()) setRailOpen(false);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [overlayRail]);

  const openRail = () => {
    if (overlayRail && isWindowedMatchRail()) setRailOpen(true);
  };
  const closeRail = () => setRailOpen(false);

  useEffect(() => {
    if (!menuOpen && !(overlayRail && railOpen)) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setRailOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    if (menuOpen) document.body.classList.add('tb-nav-lock');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('tb-nav-lock');
    };
  }, [menuOpen, overlayRail, railOpen]);

  const renderAvatar = (className = 'tb-avatar tb-avatar-badge') =>
    coolestBadge ? (
      <img src={coolestBadge.smallImageUrl} alt="" className={className} />
    ) : (
      <div className="tb-avatar" aria-hidden>
        {profile ? monogram(displayName) : '—'}
      </div>
    );

  const profileMeta = profile ? (
    <>
      {rank != null ? (
        <>
          Rank <RankPrefix rank={rank} /> ·{' '}
        </>
      ) : null}
      {score} pts
    </>
  ) : (
    'Create a profile to join'
  );

  const rankLink = profileId ? (
    <Link
      to={pathForNav('leaderboard', profileId)}
      state={{ expandProfileId: profileId } satisfies LeaderboardExpandState}
      className="tb-sidebar-meta tb-sidebar-meta--link"
      aria-label={rank != null ? `Open leaderboard at rank ${rank}` : 'Open leaderboard'}
      onClick={() => setMenuOpen(false)}
    >
      {profileMeta}
    </Link>
  ) : null;

  const profileCard = (opts: { compact?: boolean; showEmail?: boolean }) => {
    const className = `tb-sidebar-profile${opts.compact ? ' tb-sidebar-profile--compact' : ' tb-sidebar-profile--desktop'}`;
    const identity = (
      <>
        {renderAvatar()}
        <div className="tb-sidebar-profile-text">
          <p className="tb-sidebar-name">{displayName}</p>
          {opts.showEmail && verifiedEmail ? <p className="tb-sidebar-title">{verifiedEmail}</p> : null}
        </div>
      </>
    );

    if (!profile) {
      return (
        <Link
          to={pathForNav('collected', null)}
          className={`${className} tb-sidebar-profile--create`}
          aria-label="Create a profile"
          onClick={() => setMenuOpen(false)}
        >
          {identity}
          <div className="tb-sidebar-profile-rank">
            <p className="tb-sidebar-meta">{profileMeta}</p>
          </div>
        </Link>
      );
    }

    return (
      <div className={className}>
        <Link
          to={pathForNav('collected', profileId)}
          className="tb-sidebar-profile-main"
          aria-label="View your claimed badges"
        >
          {identity}
        </Link>
        <div className="tb-sidebar-profile-rank">{rankLink}</div>
      </div>
    );
  };

  return (
    <aside
      className={`tb-sidebar app-shell-sidebar${menuOpen ? ' tb-sidebar--menu-open' : ''}${
        overlayRail ? ' tb-sidebar--rail' : ''
      }${overlayRail && railOpen ? ' tb-sidebar--rail-open' : ''}`}
    >
      <div className="tb-sidebar-mobile-bar">
        {profileCard({ compact: true })}
        <button
          type="button"
          className="tb-sidebar-menu-btn"
          aria-expanded={menuOpen}
          aria-controls="tb-sidebar-panel"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <IconClose /> : <IconMenu />}
          <span className="tb-sidebar-menu-btn-label">{menuOpen ? 'Close' : 'Menu'}</span>
        </button>
      </div>

      <button
        type="button"
        className="tb-sidebar-scrim"
        aria-label="Close menu"
        tabIndex={menuOpen || railOpen ? 0 : -1}
        onClick={() => {
          setMenuOpen(false);
          closeRail();
        }}
      />

      <div
        id="tb-sidebar-panel"
        className="tb-sidebar-panel"
        onMouseEnter={openRail}
        onMouseLeave={closeRail}
        onFocusCapture={openRail}
      >
        <div className="tb-sidebar-panel-inner">
          {profileCard({ showEmail: true })}

          <div className="tb-nav-divider tb-nav-divider--after-profile" />

          <nav className="tb-nav">
            <Link to={pathForNav('feed', profileId)} className={`tb-nav-item ${activeNav === 'feed' ? 'active' : ''}`}>
              <IconHome />
              <span className="tb-nav-label">Badges Feed</span>
            </Link>
            <Link
              to={pathForNav('leaderboard', profileId)}
              className={`tb-nav-item ${activeNav === 'leaderboard' ? 'active' : ''}`}
            >
              <IconTrophy />
              <span className="tb-nav-label">Leaderboard</span>
            </Link>

            <div className="tb-nav-divider" />

            <Link
              to={pathForNav('collected', profileId)}
              className={`tb-nav-item ${activeNav === 'collected' ? 'active' : ''}`}
            >
              <IconAward />
              <span className="tb-nav-label">My Badges</span>
              <span className="tb-nav-count">{badgeCount}</span>
            </Link>
            <Link
              to={claimPath(profileId)}
              className={`tb-nav-subitem${activeNav === 'claim' ? ' active' : ' tb-nav-subitem--accent'}`}
            >
              {profileId ? 'Claim New Badges' : 'Create Profile'}
            </Link>

            {profileId ? (
              <Link
                to={pathForNav('journeys', profileId)}
                className={`tb-nav-item ${activeNav === 'journeys' ? 'active' : ''}`}
              >
                <IconFlag />
                <span className="tb-nav-label">My Journeys</span>
                <span className="tb-nav-count">{journeyCount}</span>
              </Link>
            ) : (
              <HoverTip text="Create a profile first" className="is-stretch is-below">
                <span className="tb-nav-item tb-nav-item--disabled" aria-disabled="true">
                  <IconFlag />
                  <span className="tb-nav-label">My Journeys</span>
                  <span className="tb-nav-count">{journeyCount}</span>
                </span>
              </HoverTip>
            )}

            <div className="tb-nav-divider" />

            {profileId ? (
              <Link
                to={pathForNav('profile_feed', profileId)}
                className={`tb-nav-item ${activeNav === 'profile_feed' ? 'active' : ''}`}
              >
                <IconFeed />
                <span className="tb-nav-label">My Feed</span>
                <span className="tb-nav-count">{feedCount}</span>
              </Link>
            ) : (
              <HoverTip text="Create a profile first" className="is-stretch is-below">
                <span className="tb-nav-item tb-nav-item--disabled" aria-disabled="true">
                  <IconFeed />
                  <span className="tb-nav-label">My Feed</span>
                  <span className="tb-nav-count">{feedCount}</span>
                </span>
              </HoverTip>
            )}

            {profileId ? (
              <Link
                to={pathForNav('submitted', profileId)}
                className={`tb-nav-item ${activeNav === 'submitted' ? 'active' : ''}`}
              >
                <IconRosette />
                <span className="tb-nav-label">My Contributions</span>
                <span className="tb-nav-count">{submissionsCount}</span>
              </Link>
            ) : (
              <HoverTip text="Create a profile first" className="is-stretch is-below">
                <span className="tb-nav-item tb-nav-item--disabled" aria-disabled="true">
                  <IconRosette />
                  <span className="tb-nav-label">My Contributions</span>
                  <span className="tb-nav-count">{submissionsCount}</span>
                </span>
              </HoverTip>
            )}
            <button type="button" className="tb-nav-subitem" onClick={onSubmitBadgeIdea}>
              Submit a Badge Idea
            </button>

            <div className="tb-nav-divider" />

            <Link
              to={pathForNav('badge_gallery', profileId)}
              className={`tb-nav-item ${activeNav === 'badge_gallery' ? 'active' : ''}`}
            >
              <IconGrid />
              <span className="tb-nav-label">Badges Gallery</span>
            </Link>
            <Link
              to={pathForNav('journey_gallery', profileId)}
              className={`tb-nav-item ${activeNav === 'journey_gallery' ? 'active' : ''}`}
            >
              <IconFlag />
              <span className="tb-nav-label">Journeys Gallery</span>
            </Link>

            <div className="tb-nav-divider" />

            <Link
              to={pathForNav('explore', profileId)}
              className={`tb-nav-item ${activeNav === 'explore' ? 'active' : ''}`}
            >
              <IconSearch />
              <span className="tb-nav-label">Find Profile</span>
            </Link>
            <Link
              to={pathForNav('stats', profileId)}
              className={`tb-nav-item ${activeNav === 'stats' || activeNav === 'badge_hashes' ? 'active' : ''}`}
            >
              <IconChart />
              <span className="tb-nav-label">Badges Stats</span>
            </Link>
            <div className="tb-nav-divider" />
            {tutorialDone ? (
              <Link
                to={pathForNav('badges_battle', profileId)}
                className={`tb-nav-item ${activeNav === 'badges_battle' ? 'active' : ''}`}
              >
                <IconBattle />
                <span className="tb-nav-label">Battle Badges</span>
                <span className="tb-nav-beta">Beta</span>
              </Link>
            ) : (
              <HoverTip text="Complete How to Play first" className="is-stretch is-below">
                <span className="tb-nav-item tb-nav-item--disabled" aria-disabled="true">
                  <IconBattle />
                  <span className="tb-nav-label">Battle Badges</span>
                  <span className="tb-nav-beta">Beta</span>
                </span>
              </HoverTip>
            )}
            {tutorialDone ? (
              <Link
                to={pathForNav('battle_arena', profileId)}
                className={`tb-nav-item ${activeNav === 'battle_arena' ? 'active' : ''}`}
              >
                <IconArena />
                <span className="tb-nav-label">Battle Arena</span>
                <span className="tb-nav-beta">Beta</span>
              </Link>
            ) : (
              <HoverTip text="Complete How to Play first" className="is-stretch is-below">
                <span className="tb-nav-item tb-nav-item--disabled" aria-disabled="true">
                  <IconArena />
                  <span className="tb-nav-label">Battle Arena</span>
                  <span className="tb-nav-beta">Beta</span>
                </span>
              </HoverTip>
            )}
            <Link
              to={pathForNav('battle_how_to', profileId)}
              className={`tb-nav-subitem${tutorialDone ? '' : ' tb-nav-subitem--accent'}${
                activeNav === 'battle_how_to' ? ' active' : ''
              }`}
            >
              How to Play
            </Link>
            <Link
              to={pathForNav('battle_leaderboards', profileId)}
              className={`tb-nav-subitem${activeNav === 'battle_leaderboards' ? ' active' : ''}`}
            >
              Battle Leaderboard
            </Link>
          </nav>

          <div className="tb-sidebar-footer">
            <div className="tb-nav-divider" />
            <a href={RULES_SETUP_URL} target="_blank" rel="noopener noreferrer" className="tb-sidebar-rules">
              <IconBook />
              <span className="tb-nav-label">Rules & Setup</span>
            </a>
            <button
              type="button"
              className="tb-sign-out"
              onClick={() => {
                if (window.confirm('Are you sure you want to sign out?')) onSignOut();
              }}
            >
              <IconSignOut />
              <span className="tb-nav-label">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};
